import { zodResolver } from '@hookform/resolvers/zod'
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    Typography
} from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { AsyncSelect, Button, ControlledTextField } from 'src/components/forms'
import { EntityWithIdFields } from 'src/types'
import { z } from 'zod'
import { UnidadNegocioViewDTO, UnidadNegocioViewDTOFields } from './types'
import { HttpUnidadNegocio } from './api'
import { HttpSystems } from 'src/http/workflow/httpSystems'
import { usePaisLogo } from 'src/hooks/usePaisLogo'

type EditUnidadNegocioProps = {
    unidadNegocio?: UnidadNegocioViewDTO
    onClose: () => void
}

const schema = z.object({
    [UnidadNegocioViewDTOFields.Nombre]: z.string().min(1, 'Requerido'),
    [UnidadNegocioViewDTOFields.CodPais]: z.number().nullable()
})

type FormData = z.infer<typeof schema>

function EditUnidadNegocio({ unidadNegocio, onClose }: EditUnidadNegocioProps) {
    const isNew = !unidadNegocio || unidadNegocio[EntityWithIdFields.Id] === 0
    const { paisLogos } = usePaisLogo()

    const { control, handleSubmit } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: isNew
            ? { [UnidadNegocioViewDTOFields.Nombre]: '', [UnidadNegocioViewDTOFields.CodPais]: null }
            : { ...unidadNegocio }
    })

    const onSubmitWrapper = () => {
        let mensajeError = 'No se pudo guardar'

        const promise = new Promise<void>((resolve, reject) => {
            handleSubmit(
                formData => {
                    const request = isNew
                        ? HttpUnidadNegocio.insert(formData)
                        : HttpUnidadNegocio.update(unidadNegocio![EntityWithIdFields.Id], formData)

                    request
                        .then(() => {
                            setTimeout(onClose, 500)
                            resolve()
                        })
                        .catch(reject)
                },
                () => {
                    mensajeError = 'Revise el formulario'
                    reject(mensajeError)
                }
            )()
        })

        toast.promise(promise, {
            success: 'Guardado',
            error: () => mensajeError,
            loading: 'Guardando'
        })

        return promise
    }

    return (
        <>
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction='row' spacing={1.5} alignItems='center'>
                    <BusinessIcon color='primary' />
                    <Typography variant='h6' fontWeight={600}>
                        {isNew ? 'Nueva unidad de negocio' : 'Editar unidad de negocio'}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: '24px !important', px: 5, pb: 4 }}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name={UnidadNegocioViewDTOFields.Nombre}
                            label='Nombre'
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AsyncSelect
                            control={control}
                            name={UnidadNegocioViewDTOFields.CodPais}
                            label='País'
                            loadOptions={() => Promise.resolve(paisLogos)}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant='text' color='inherit' onClick={onClose}>
                    Cancelar
                </Button>
                <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                    Guardar
                </Button>
            </DialogActions>
        </>
    )
}

export default EditUnidadNegocio
